import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowDownRight, Clock, FileText, CheckCircle2 } from 'lucide-react';
interface OverviewTabProps {
  onNavigate: (tab: any) => void;
  onSelectQuote: (id: string) => void;
  quotes: any[];
}
export function OverviewTab({
  onNavigate,
  onSelectQuote,
  quotes
}: OverviewTabProps) {
  const recentQuotes = quotes.slice(0, 5);
  const activeHolds = quotes.filter(q => q.status === 'Hold Placed').slice(0, 3);
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Fully Booked':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200 font-bold';
      case 'Contract Issued':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'Deposit Pending':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Hold Placed':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Approved':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Viewed':
        return 'bg-sky-100 text-sky-700 border-sky-200';
      case 'Draft':
      case 'Submitted':
      case 'Pending Review':
        return 'bg-stone-100 text-stone-700 border-stone-200';
      case 'Rejected':
      case 'Lost':
      case 'Expired':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-stone-100 text-stone-700 border-stone-200';
    }
  };
  return <div className="space-y-8 animate-in fade-in duration-500" data-id="element-841">
      <div data-id="element-842">
        <h1 className="font-serif text-3xl text-stone-900 mb-2" data-id="element-843">
          Dashboard Overview
        </h1>
        <p className="text-stone-500" data-id="element-844">
          Welcome back. Here's what's happening across your properties today.
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-845">
        <div className="bg-white p-6 border border-stone-200 shadow-sm" data-id="element-846">
          <div className="flex justify-between items-start mb-4" data-id="element-847">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-848">
              Fully Booked Revenue (YTD)
            </p>
            <span className="flex items-center text-xs font-mono text-emerald-600 bg-emerald-50 px-2 py-1 rounded" data-id="element-849">
              <ArrowUpRight className="w-3 h-3 mr-1" data-id="element-850" />
              24%
            </span>
          </div>
          <p className="font-mono text-3xl text-stone-900" data-id="element-851">$4.25M</p>
        </div>

        <div className="bg-white p-6 border border-stone-200 shadow-sm" data-id="element-852">
          <div className="flex justify-between items-start mb-4" data-id="element-853">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-854">
              Leakage Prevented
            </p>
            <span className="flex items-center text-xs font-mono text-emerald-600 bg-emerald-50 px-2 py-1 rounded" data-id="element-855">
              <ArrowUpRight className="w-3 h-3 mr-1" data-id="element-856" />
              12%
            </span>
          </div>
          <p className="font-mono text-3xl text-stone-900" data-id="element-857">$382.5K</p>
        </div>

        <div className="bg-white p-6 border border-stone-200 shadow-sm" data-id="element-858">
          <div className="flex justify-between items-start mb-4" data-id="element-859">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-860">
              Active Quotes
            </p>
          </div>
          <p className="font-mono text-3xl text-stone-900" data-id="element-861">42</p>
          <p className="text-sm text-stone-500 mt-2" data-id="element-862">
            18 pending agency review
          </p>
        </div>

        <div className="bg-white p-6 border border-stone-200 shadow-sm" data-id="element-863">
          <div className="flex justify-between items-start mb-4" data-id="element-864">
            <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-865">
              Conversion Rate
            </p>
            <span className="flex items-center text-xs font-mono text-emerald-600 bg-emerald-50 px-2 py-1 rounded" data-id="element-866">
              <ArrowUpRight className="w-3 h-3 mr-1" data-id="element-867" />
              4.2%
            </span>
          </div>
          <p className="font-mono text-3xl text-stone-900" data-id="element-868">38%</p>
          <p className="text-sm text-stone-500 mt-2" data-id="element-869">
            Avg. time to close: 2.4 days
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-870">
        {/* Recent Quotes */}
        <div className="lg:col-span-2 bg-white border border-stone-200 shadow-sm" data-id="element-871">
          <div className="flex items-center justify-between p-6 border-b border-stone-200" data-id="element-872">
            <h2 className="font-serif text-xl text-stone-900" data-id="element-873">Recent Quotes</h2>
            <button onClick={() => onNavigate('quotes')} className="text-sm font-medium text-red-600 hover:text-red-700 transition-colors" data-id="element-874">
              View All
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-875">
            <table className="w-full text-left text-sm" data-id="element-876">
              <thead className="bg-stone-50 border-b border-stone-200" data-id="element-877">
                <tr data-id="element-878">
                  <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-879">
                    Group / Agency
                  </th>
                  <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-880">
                    Dates
                  </th>
                  <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-881">
                    Value
                  </th>
                  <th className="p-4 font-mono text-xs uppercase tracking-widest text-stone-500 font-medium" data-id="element-882">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100" data-id="element-883">
                {recentQuotes.map(quote => <tr key={quote.id} onClick={() => onSelectQuote(quote.id)} className="hover:bg-stone-50 cursor-pointer transition-colors group" data-id="element-884">
                    <td className="p-4" data-id="element-885">
                      <p className="font-medium text-stone-900 group-hover:text-red-600 transition-colors" data-id="element-886">
                        {quote.groupName}
                      </p>
                      <p className="text-stone-500" data-id="element-887">{quote.agency}</p>
                    </td>
                    <td className="p-4 text-stone-600" data-id="element-888">{quote.dates}</td>
                    <td className="p-4 font-mono text-stone-900" data-id="element-889">
                      ${quote.value.toLocaleString()}
                    </td>
                    <td className="p-4" data-id="element-890">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-mono border ${getStatusColor(quote.status)}`} data-id="element-891">
                        {quote.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {/* Active Holds */}
        <div className="bg-white border border-stone-200 shadow-sm flex flex-col" data-id="element-892">
          <div className="flex items-center justify-between p-6 border-b border-stone-200" data-id="element-893">
            <h2 className="font-serif text-xl text-stone-900" data-id="element-894">
              Expiring Holds
            </h2>
            <button onClick={() => onNavigate('holds')} className="text-sm font-medium text-red-600 hover:text-red-700 transition-colors" data-id="element-895">
              View All
            </button>
          </div>
          <div className="p-6 flex-1 flex flex-col gap-4" data-id="element-896">
            {activeHolds.length > 0 ? activeHolds.map(hold => <div key={hold.id} onClick={() => onSelectQuote(hold.id)} className="p-4 border border-stone-200 rounded-md hover:border-red-300 hover:bg-red-50/50 cursor-pointer transition-all" data-id="element-897">
                  <div className="flex justify-between items-start mb-2" data-id="element-898">
                    <p className="font-medium text-stone-900" data-id="element-899">
                      {hold.groupName}
                    </p>
                    <span className="flex items-center text-xs font-mono text-red-600 bg-red-50 px-2 py-1 rounded border border-red-100" data-id="element-900">
                      <Clock className="w-3 h-3 mr-1" data-id="element-901" />
                      {hold.expiryHours}h left
                    </span>
                  </div>
                  <p className="text-sm text-stone-500 mb-3" data-id="element-902">
                    {hold.rooms} rooms • {hold.property}
                  </p>
                  <div className="w-full bg-stone-100 h-1.5 rounded-full overflow-hidden" data-id="element-903">
                    <div className="bg-red-500 h-full rounded-full" style={{
                width: `${hold.expiryHours / 72 * 100}%`
              }} data-id="element-904" />
                  </div>
                </div>) : <div className="flex-1 flex flex-col items-center justify-center text-center p-6" data-id="element-905">
                <CheckCircle2 className="w-8 h-8 text-stone-300 mb-3" data-id="element-906" />
                <p className="text-stone-500" data-id="element-907">No holds expiring soon.</p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}